import React, { useEffect } from 'react';
import DocumentTitle from 'react-document-title';
import Routes from 'utils/routes.js.erb';
import { authHeaders } from 'actions';
import { errorMessage } from 'actions/internal';
import TopNavBar from 'containers/nav/TopNavBar';
import SubNavBar from 'containers/nav/SubNavBar';
import { PaginationComponent } from 'components/helpers/TableFooter';
import AdministrationSubNav from 'components/administration/AdministrationSubNav';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import Select from 'react-select';
import EditSearch from 'components/filters/EditSearch';
import { appNamedRoutes } from 'routes';
import {
  keepPreviousData,
  useQuery,
} from '@tanstack/react-query'
import { showLoading, hideLoading } from 'react-redux-loading-bar';
import axios from 'axios'
import { useQueryParam } from 'use-query-params';
import _ from 'lodash';
import LoadingOverlay from 'react-loading-overlay-nextgen';
import { LoadingSpinner } from 'components/helpers/LoadingSpinner';
import { groupByDisabled } from 'utils/select';

const AdministrationFamiliesPage = ({
  history,
  onShowLoading,
  onHideLoading,
  onErrorMessage,
}) => {
  const [page, setPage] = useQueryParam('page', 1);
  const [search, setSearch] = useQueryParam('search', null);
  const [groupId, setGroupId] = useQueryParam('group_id', null);
  const [subgroupId, setSubgroupId] = useQueryParam('subgroup_id', null);
  const [cohortId, setCohortId] = useQueryParam('cohort_id', null);

  const accountsQuery = useQuery({
    queryKey: ['administration', 'accounts', groupId, subgroupId, cohortId, search, page],
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => axios.get(
      Routes.api_dashboard_administration_accounts_path({
        search,
        group_id: groupId,
        subgroup_id: subgroupId,
        cohort_id: cohortId,
        page: page,
      }),
      { headers: authHeaders(), signal }
    ).then(response => response.data),
    onError: (error) => {
      onErrorMessage(error.response.data.message);
    },
  })

  const filtersQuery = useQuery({
    queryKey: ['administration', 'filters'],
    queryFn: ({ signal }) => axios.get(
      Routes.api_dashboard_administration_filters_path(),
      { headers: authHeaders(), signal }
    ).then(response => response.data),
    onError: (error) => {
      onErrorMessage(error.response.data.message);
    },
  })

  useEffect(() => {
    if (accountsQuery.isPending) {
      onShowLoading();
    } else {
      onHideLoading();
    }

    if (filtersQuery.isPending) {
      onShowLoading();
    } else {
      onHideLoading();
    }
  }, [accountsQuery.isPending, filtersQuery.isPending, onHideLoading, onShowLoading]);

  if (!filtersQuery.data) {
    return (<DocumentTitle title='Sparkler - Administration'>
      <div>
        <TopNavBar />
        <SubNavBar />
        <AdministrationSubNav />
        <div className="row content">
          <div className="column">
            <LoadingSpinner bgColor='light' />
          </div>
        </div>
      </div>
    </DocumentTitle>
    );
  }

  return (<DocumentTitle title='Sparkler - Administration'>
    <div>
      <TopNavBar />
      <SubNavBar />
      <AdministrationSubNav />
      <div className="row">
        <div className="column">
          <div className='row u-margin-top--large'>
            <div className='column small-12 u-margin-bottom--base'>
              <EditSearch placeholder='Search by name, email...' value={search} onChange={(value) => {
                setPage(1)
                setSearch(value)
              }} />
            </div>
          </div>
          <div className='row'>
            {filtersQuery.data.groups.filter(s => !s.disabled).length > 1 && <div className='column small-12 medium-3 u-margin-bottom--base'>
              <Select
                name='sp-group-select'
                className={'o-select o-select--optgroup'}
                isMulti
                value={filtersQuery.data.groups.filter(v => (groupId || '').split(',').map(i => parseInt(i)).includes(v.value))}
                onChange={(value) => {
                  setPage(1)
                  setGroupId(value.map(v => v.value))
                }}
                options={filtersQuery.data.groups}
                placeholder='All Groups'
              />
            </div>}
            {filtersQuery.data.subgroups.filter(s => !s.disabled).length > 1 && <div className='column small-12 medium-3 u-margin-bottom--base'>
              <Select
                name='sp-group-select'
                className={'o-select o-select--optgroup'}
                isMulti
                value={filtersQuery.data.subgroups.filter(v => (subgroupId || '').split(',').map(i => parseInt(i)).includes(v.value))}
                onChange={(value) => {
                  setPage(1)
                  setSubgroupId(value.map(v => v.value))
                }}
                options={groupByDisabled(filtersQuery.data.subgroups)}
                placeholder='All Subgroups'
              />
            </div>}
            {filtersQuery.data.cohorts.filter(s => !s.disabled).length > 1 && <div className='column small-12 medium-3 u-margin-bottom--base'>
              <Select
                name='sp-cohort-select'
                className={'o-select o-select--optgroup'}
                isMulti
                value={filtersQuery.data.cohorts.filter(v => (cohortId || '').split(',').map(i => parseInt(i)).includes(v.value))}
                onChange={(value) => {
                  setPage(1)
                  setCohortId(value.map(v => v.value))
                }}
                options={groupByDisabled(filtersQuery.data.cohorts)}
                placeholder='All Cohorts'
              />
            </div>}
          </div>
          <div className='row u-margin-top--small'>
            <div className='column small-12 u-margin-bottom--base'>
              <span
                className='o-sp-button o-sp-button--update o-sp-button--primary u-float--right'
                onClick={() => {
                  const queryParams = history.location.search;
                  history.push(`${appNamedRoutes.administrationFamilies}/new${queryParams}`)
                }}
              >
                Add New Family
              </span>
            </div>
          </div>
          <div className='u-margin-bottom--gutter2x'>
            <LoadingOverlay
              active={accountsQuery.isFetching}
              spinner={<LoadingSpinner />}
            >
              <div className='table-scroll'>
                <a className='faq' href={window.ADMINISTRATION_YOUR_FAMILIES_FAQ_URL} target='_blank' rel="noreferrer">
                  <i className='fa fa-question-circle' style={{ fontSize: 20, color: '#31ccff' }}></i>
                </a>
                <table className='o-table o-table--bordered'>
                  <thead>
                    <tr>
                      <th style={{ width: 120 }}>Id</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Children</th>
                      <th>Last Action</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {_.get(accountsQuery, 'data.records', []).length === 0 ? <tr><td colSpan='6'>No records found</td></tr> :
                      _.get(accountsQuery, 'data.records', []).map((account) => (
                        <tr className='u-pointer' key={account.id} onClick={(event) => {
                          if (event.target.className !== 'show-more-less-clickable') {
                            const queryParams = history.location.search;
                            history.push(`${appNamedRoutes.administrationFamilies}/${account.id}/edit${queryParams}`)
                          }
                        }}>
                          <td>{account.id}</td>
                          <td>{account.name}</td>
                          <td>{account.email}</td>
                          <td>
                            <ul>
                              {account.players.map((player, index) => (
                                <li key={index}>{player.id} - {player.name}</li>
                              ))}
                            </ul>
                          </td>
                          <td>{account.last_action_date && moment(account.last_action_date).format('MM/DD/YYYY')}</td>
                          <td>{moment(account.created_at).format('MM/DD/YYYY')}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              {_.get(accountsQuery, 'data.pagination.total_count', 0) > 0 && <PaginationComponent
                activePage={accountsQuery.data.pagination.current_page}
                itemsCountPerPage={accountsQuery.data.pagination.per_page}
                totalItemsCount={accountsQuery.data.pagination.total_count}
                onPageChange={i => setPage(i)} />}
            </LoadingOverlay>
          </div>
        </div>
      </div>
    </div>
  </DocumentTitle>
  );
}

AdministrationFamiliesPage.propTypes = {
  history: PropTypes.object.isRequired,
  onShowLoading: PropTypes.func.isRequired,
  onHideLoading: PropTypes.func.isRequired,
  onErrorMessage: PropTypes.func.isRequired,
}

const mapStateToProps = () => {
  return {}
}

const mapDispatchToProps = (dispatch) => {
  return {
    onErrorMessage: (message) => {
      dispatch(errorMessage(message))
    },
    onShowLoading: () => {
      dispatch(showLoading())
    },
    onHideLoading: () => {
      dispatch(hideLoading())
    },
  }
};


export default connect(mapStateToProps, mapDispatchToProps)(AdministrationFamiliesPage);
